import { IconButton } from '@mui/joy';
import { Plus, X } from '@phosphor-icons/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
} from '~/components/ui/dialog';
import { InputField } from '~/components/ui/input';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import { ProcessService } from '~/services/api/processes/endpoints';
import { newTemplateSchema } from '~/services/api/processes/types';
import { useWrappedMutation } from '~/services/api/rest-api';
import { useForm } from '~/utils/forms';

export const NewTemplateDialog = ({
  isPartner = false,
}: {
  isPartner?: boolean;
}) => {
  const { createToastNotification } = useToastNotificationContext();
  const intl = useIntl();
  const createTemplate = useWrappedMutation(
    ProcessService.endpoints.createProcessTemplate.useMutation(),
  );
  const createPartnerTemplate = useWrappedMutation(
    ProcessService.endpoints.createPartnerProcessTemplate.useMutation(),
  );
  const navigate = useNavigate();
  const form = useForm({
    schema: newTemplateSchema,
  });

  const handleSubmit = form.handleSubmit(async (data) => {
    if (isPartner) {
      await createPartnerTemplate.mutateAsync(
        {
          payload: data,
          params: { is_partner_template: 'true' },
        },
        {
          onSuccess: (res) => {
            form.reset();
            createToastNotification(
              intl.formatMessage({ defaultMessage: 'Template added.' }),
              'success',
            );
            navigate(
              `/customer/backoffice/settings/process-template/${res.id}`,
            );
          },
        },
      );
    } else {
      await createTemplate.mutateAsync(data, {
        onSuccess: (res) => {
          form.reset();
          createToastNotification(
            intl.formatMessage({ defaultMessage: 'Template added.' }),
            'success',
          );
          navigate(`/customer/settings/templates/processes/${res.id}`);
        },
      });
    }
  });

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant='soft'
          color='neutral'
          sx={{
            textTransform: 'none',
          }}
          startDecorator={<Plus />}
          type='button'
        >
          <FormattedMessage defaultMessage='Create template' />
        </Button>
      </DialogTrigger>
      <DialogContent size='xl' className='space-y-4'>
        <div className='flex flex-row justify-between items-center'>
          <DialogTitle>
            <FormattedMessage defaultMessage='New Template' />
          </DialogTitle>
          <DialogClose asChild>
            <IconButton size='lg'>
              <X size={20} />
            </IconButton>
          </DialogClose>
        </div>
        <form onSubmit={handleSubmit} className='space-y-3'>
          <InputField
            control={form.control}
            field='name'
            placeholder={intl.formatMessage({
              defaultMessage: 'Template Name',
            })}
          />
          <DialogFooter>
            <Button
              variant='solid'
              color='secondary'
              type='submit'
              disabled={form.formState.isSubmitting}
            >
              <FormattedMessage defaultMessage='Create template' />
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
